import Splide from '@splidejs/splide';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import Vue from '../../assets/vue';
import { setMaxHeightStyle } from '../../scripts/utils/setMaxHeightStyle';
import { addItems } from '../../scripts/cartApi';
import { sendCustomEvent } from '../../scripts/config/analytics';
import {
  ADD_PRODUCT_TO_CART_ERROR_MSG,
  addOffsetToStickyProductSlider,
  calcBundlePreOrderMessage,
  calcBundleVariantsForSell,
  calcNetPrice,
  calcSalePrice,
  findUnavailableBundleVariant,
  forceProductVideoStart,
  formatPriceWithoutDecimals,
  formatPriceWithoutZeros,
  getVariantIdByUrl,
  isBundleAvailable,
  normalizeProductConfig,
  updateAvailabilityAddToCartNavBtns,
  updateProductUrl,
  videoEnded,
  videoToggle,
} from '../../scripts/utils/product-helpers';
import {
  initCart,
  openSideCart,
} from '../../templates/common/side-cart/side-cart';
import {
  initProductBuyBanner,
  showBannerSpinner,
} from '../../templates/common/product-buy-banner/product-buy-banner';
import { calcBogoBonusProducts } from '../../scripts/utils/bogo-helpers';
import {
  ADD_TO_CART_TEXT,
  NOT_AVAILABLE_TEXT,
  PRE_ORDER_TEXT,
} from '../../scripts/config/constants';
import { initProductWishListModal } from '../product-wish-list-modal/product-wish-list-modal';

const { DeviceHelper, productsConfig } = window;
const INTEGRATOR_PAGE_PATH = '/pages/builders-find-a-brilliant-integrator';

const findActiveVariantByOption = (variants, activeOption) =>
  variants.find((variant) => variant.handleizedOption1 === activeOption) ||
  variants[0];

const findActiveVariantByVariantId = (variants, variantId) =>
  variants.find(({ id }) => Number(id) === Number(variantId)) || variants[0];

const initSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    pagination: true,
    accessibility: true,
    trimSpace: true,
    perMove: 1,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    slideFocus: false,
    updateOnMove: true,
    cover: false,
    focusableNodes: 'button',
    lazyLoad: 'nearby',
    classes: {
      pagination: 'product-base-poe__slider-main-pagination',
      page: 'product-base-poe__slider-main-pagination-btn',
    },
  });

const initThumbSlider = (selector) =>
  new Splide(selector, {
    type: 'loop',
    direction: 'ttb',
    height: '100%',
    gap: 10,
    pagination: false,
    accessibility: true,
    isNavigation: true,
    perPage: 6,
    flickVelocityThreshold: 0.5,
    flickPower: 500,
    wheel: true,
    wheelSleep: 300,
    lazyLoad: 'nearby',
  });

const Product = {
  data() {
    const {
      activeProductId,
      products,
      productsMeta,
      variantsMeta,
      variantsInventory,
      bundleConfig,
      warrantyConfig,
    } = productsConfig;
    const productActiveConfig = products[activeProductId];
    const normalizedProductActive = normalizeProductConfig(productActiveConfig);
    const variantIdByUrl = getVariantIdByUrl(productActiveConfig);
    const normalizedVariantActive = findActiveVariantByVariantId(
      normalizedProductActive.variants,
      variantIdByUrl
    );
    const variantActiveOption = normalizedVariantActive.handleizedOption1;
    const variantActiveIndex =
      normalizedProductActive.variants.indexOf(normalizedVariantActive) || 0;

    this.normalizedRelatedProducts = Object.values(products).reduce(
      (acc, product) => {
        acc.push(normalizeProductConfig(product));
        return acc;
      },
      []
    );
    const isTabletViewport = DeviceHelper.isTabletViewport();

    return {
      normalizedProductActive,
      normalizedVariantActive,
      productsMeta,
      variantActiveOption,
      variantActiveIndex,
      variantsMeta,
      variantsInventory,
      bundleConfig,
      warrantyConfig,
      isTabletViewport,
      isSpinnerMainProductActive: false,
      errorMessage: '',
      isDetailsBlockClose: true,
      isDescriptionBlockClose: true,
      isBenefitsBlockClose: true,
      loaded: false,
      hasWarranty: false,
    };
  },
  computed: {
    productActiveMeta() {
      return this.productsMeta[this.normalizedProductActive.id];
    },
    variantActiveMeta() {
      return this.variantsMeta[this.normalizedVariantActive.id];
    },
    variantActiveBundleConfig() {
      return this.bundleConfig[this.normalizedVariantActive.id] || null;
    },
    variantActiveInventory() {
      return this.variantsInventory[this.normalizedVariantActive.id];
    },
    variantActiveWarrantyConfig() {
      return this.warrantyConfig[this.normalizedVariantActive.id] || null;
    },
    buyBannerProducts() {
      return this.normalizedRelatedProducts.reduce((acc, normalizedProduct) => {
        acc.push({
          titleShort:
            this.productsMeta[normalizedProduct.id].buyBannerTitleShort,
          productId: normalizedProduct.id,
        });

        return acc;
      }, []);
    },
    sliderMedias() {
      return (
        productsConfig.variantsMedia[this.normalizedVariantActive.id]
          .sliderMedia || []
      );
    },
    isVariantActiveAvailable() {
      const { isVariantInStock, normalizedVariantActive, errorMessage } = this;

      const isActiveVariantInStock = isVariantInStock(normalizedVariantActive);
      const isErrorEmpty = errorMessage === '';

      return isActiveVariantInStock && isErrorEmpty;
    },
    preOrderMessage() {
      const { preOrderText } = this.variantActiveMeta;

      return this.variantActiveBundleConfig
        ? calcBundlePreOrderMessage(
            this.variantActiveBundleConfig,
            preOrderText
          )
        : '';
    },
    submitButtonText() {
      if (!this.isVariantActiveAvailable) {
        return this.variantActiveMeta.notAvailableText || NOT_AVAILABLE_TEXT;
      }

      if (this.preOrderMessage) {
        return PRE_ORDER_TEXT;
      }

      return ADD_TO_CART_TEXT;
    },
    swatchesList() {
      return this.normalizedProductActive.variants.reduce(
        (swatchesData, variant) => {
          swatchesData.push({
            available: this.isVariantInStock(variant),
            variantId: variant.id,
            optionValue: variant.handleizedOption1,
          });

          return swatchesData;
        },
        []
      );
    },
    activeNetPrice() {
      const { activeDiscountPrice, normalizedVariantActive } = this;

      return calcNetPrice(
        normalizedVariantActive.price,
        normalizedVariantActive.compare_at_price,
        activeDiscountPrice
      );
    },
    formattedActiveNetPrice() {
      return formatPriceWithoutZeros(this.activeNetPrice);
    },
    activeDiscountPrice() {
      const { discountPrice } = this.variantActiveMeta;

      if (discountPrice) {
        return discountPrice;
      }

      return null;
    },
    activeSalePrice() {
      const { price } = this.normalizedVariantActive;
      const { discountPrice } = this.variantActiveMeta;

      return calcSalePrice(price, discountPrice);
    },
    formattedActiveSalePrice() {
      return formatPriceWithoutZeros(this.activeSalePrice);
    },
    formattedWarrantyPrice() {
      const price =
        this.variantActiveWarrantyConfig?.variants.reduce(
          (acc, variant) => (variant.price ? acc + variant.price : acc),
          0
        ) || null;

      return formatPriceWithoutDecimals(price);
    },
    unavailableItem() {
      return findUnavailableBundleVariant(this.variantActiveBundleConfig);
    },
    hasWarrantyProduct() {
      return Boolean(this.variantActiveWarrantyConfig);
    },
  },
  methods: {
    productToggleVideo(event) {
      const element = event.currentTarget;

      videoToggle(element);
    },
    productEndedVideo(event) {
      const videoEl = event.target;

      videoEnded(videoEl);
    },
    changeActiveProduct(productId) {
      const normalizedProductNext =
        this.normalizedRelatedProducts.find(({ id }) => id === productId) ||
        null;

      if (normalizedProductNext) {
        this.normalizedProductActive = normalizedProductNext;
        this.normalizedVariantActive = findActiveVariantByOption(
          this.normalizedProductActive.variants,
          this.variantActiveOption
        );
        this.errorMessage = '';

        Vue.nextTick(() => {
          this.imagesSliderMain.refresh();
          this.imagesSliderThumbs.refresh();
          this.imagesSliderMain.go(0);

          updateProductUrl(
            this.normalizedVariantActive.id,
            this.normalizedProductActive.url
          );

          setMaxHeightStyle(
            '[data-product-base-poe-product-mobile-panel-features-content]'
          );
          updateAvailabilityAddToCartNavBtns();
        });
      }
    },
    changeActiveVariant(variantId, index) {
      const variantNext =
        this.normalizedProductActive.variants.find(
          (variant) => variant.id === variantId
        ) || null;

      if (variantNext) {
        this.normalizedVariantActive = variantNext;
        this.variantActiveOption =
          this.normalizedVariantActive.handleizedOption1;
        this.variantActiveIndex = index;
        this.errorMessage = '';
      }

      Vue.nextTick(() => {
        this.imagesSliderMain.refresh();
        this.imagesSliderThumbs.refresh();

        updateProductUrl(
          this.normalizedVariantActive.id,
          this.normalizedProductActive.url
        );
      });
    },
    calcFormattedSalePrice(price, discountPrice = null) {
      const salePrice = calcSalePrice(price, discountPrice);

      return formatPriceWithoutZeros(salePrice);
    },
    calcFormattedNetPrice(price, compareAtPrice = null, discountPrice = null) {
      const netPrice = calcNetPrice(price, compareAtPrice, discountPrice);

      return formatPriceWithoutZeros(netPrice);
    },
    calcVariantsForSellBuyList(withWarranty) {
      const config = withWarranty
        ? this.variantActiveWarrantyConfig
        : this.variantActiveBundleConfig;

      // Main
      const mainProducts = calcBundleVariantsForSell(config, false);

      // Bogo
      const bogoProducts = mainProducts
        .reduce((acc, { id }) => {
          if (productsConfig.bogo[id]?.is_active) {
            acc.push(...calcBogoBonusProducts(productsConfig.bogo[id]));
          }

          return acc;
        }, [])
        .filter(Boolean);

      return [...mainProducts, ...bogoProducts];
    },
    redirectToIntegratorPage(e) {
      e.preventDefault();
      window.open(INTEGRATOR_PAGE_PATH, '_blank', 'noopener,noreferrer');
    },
    async submitProductForm(e, withWarranty = false) {
      e.preventDefault();

      this.isSpinnerMainProductActive = true;
      showBannerSpinner(true);

      await initCart();

      const items = this.calcVariantsForSellBuyList(withWarranty);

      try {
        await addItems(items);

        const { sideCart } = window;

        sideCart.updateView(null, openSideCart);

        const addedVariantsIds = items.map((item) => item.id);

        addedVariantsIds.forEach((addedVariantId) => {
          sendCustomEvent({
            category: 'Cart',
            action: 'Add',
            label: addedVariantId,
          });
        });
      } catch (err) {
        if (err.headers) {
          err.json().then((body) => {
            this.errorMessage = body.message;
          });
        } else {
          this.errorMessage = ADD_PRODUCT_TO_CART_ERROR_MSG;
        }
      }

      this.isSpinnerMainProductActive = false;
      showBannerSpinner(false);
    },
    makeTooltipTextTop(normalizedVariant) {
      return `${normalizedVariant.option1}`;
    },
    makeTooltipTextBottom(normalizedVariant) {
      if (
        normalizedVariant.available &&
        this.normalizedProductActive.available
      ) {
        const isVariantAnExtraFrame =
          this.normalizedProductActive.variants.indexOf(normalizedVariant) !==
          0;

        if (isVariantAnExtraFrame) {
          return `Sold Separately`;
        }

        return 'Included';
      }
      if (!this.normalizedProductActive.available) {
        return `Contact A Pro`;
      }
      return `Available for Pre-Order`;
    },
    isVariantInStock(normalizedVariant) {
      const areAllVariantsAvailable = isBundleAvailable(
        this.bundleConfig[normalizedVariant.id]
      );

      return normalizedVariant.available && areAllVariantsAvailable;
    },
    selectPanelImages(product) {
      const relatedVariant =
        productsConfig.products[product.id].variants[this.variantActiveIndex];

      return productsConfig.variantsMedia[relatedVariant.id].selectModelMedia;
    },
    openWishListModal() {
      window.productWishListModal?.openModal();
    },
  },
  mounted() {
    updateAvailabilityAddToCartNavBtns();

    const variantId = getVariantIdByUrl(
      productsConfig.products[productsConfig.activeProductId]
    );

    if (variantId) {
      updateProductUrl(variantId, this.normalizedProductActive.url);
    }
    const mainSliderEl = document.querySelector('[data-product-slider-main]');
    const thumbsSliderEL = document.querySelector(
      '[data-product-slider-thumbs]'
    );

    if (mainSliderEl && thumbsSliderEL) {
      this.imagesSliderMain = initSlider('[data-product-slider-main]');
      this.imagesSliderThumbs = initThumbSlider('[data-product-slider-thumbs]');

      if (this.imagesSliderMain) {
        this.imagesSliderMain.on('active', ({ index }) => {
          const isVideoType = this.sliderMedias[index]?.type === 'video';

          if (isVideoType) {
            forceProductVideoStart(index, DeviceHelper.isMobileViewport());
          }
        });

        this.imagesSliderMain.on('lazyload:loaded', () => {
          this.loaded = true;
        });
        this.imagesSliderMain.sync(this.imagesSliderThumbs);
        this.imagesSliderMain.mount();
        this.imagesSliderThumbs.mount();
      }
    }

    forceProductVideoStart(0, DeviceHelper.isMobileViewport());

    window.addEventListener('load', () => {
      if (!this.isTabletViewport && mainSliderEl) {
        const mainImageSliderHeight = mainSliderEl.offsetHeight;

        thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
      }

      initProductBuyBanner({
        productId: this.normalizedProductActive.id,
        variantId: this.normalizedVariantActive.id,
        productsList: this.buyBannerProducts,
        swatchesList: this.swatchesList,
        isPOEProduct: true,
        formattedSalePrice: this.formattedActiveSalePrice,
        hasCrossedPrice: Boolean(
          this.activeDiscountPrice ||
            this.normalizedVariantActive.compare_at_price
        ),
        formattedNetPrice: this.formattedActiveNetPrice,
        isVariantActiveAvailable: this.isVariantActiveAvailable,
        submitButtonText: this.submitButtonText,
      });

      initProductWishListModal({
        variantId: this.unavailableItem?.id || null,
      });
    });

    window.addEventListener(
      'resize',
      throttle(() => {
        this.isTabletViewport = DeviceHelper.isTabletViewport();

        if (!this.isTabletViewport && mainSliderEl) {
          const mainImageSliderHeight = mainSliderEl.offsetHeight;

          thumbsSliderEL.style.height = `${mainImageSliderHeight}px`;
        }
      }, 100)
    );
  },
  updated() {
    const { productWishListModal, productBuyBanner } = window;

    productBuyBanner?.updateBannerStates({
      newProductId: this.normalizedProductActive.id,
      newVariantId: this.normalizedVariantActive.id,
      newProductsList: this.buyBannerProducts,
      newSwatchesList: this.swatchesList,
      newFormattedSalePrice: this.formattedActiveSalePrice,
      hasNewCrossedPrice: Boolean(
        this.activeDiscountPrice ||
          this.normalizedVariantActive.compare_at_price
      ),
      newFormattedNetPrice: this.formattedActiveNetPrice,
      isNewVariantAvailable: this.isVariantActiveAvailable,
      newSubmitButtonText: this.submitButtonText,
    });

    if (this.unavailableItem) {
      productWishListModal?.updateModalState({
        newVariantId: this.unavailableItem.id,
      });
    }
  },
};

export const initPoeProduct = () => {
  if (Product) {
    new Vue(Product).$mount('#product-base-poe');
  }

  window.addEventListener(
    'resize',
    debounce(() => {
      setMaxHeightStyle(
        '[data-product-base-poe-product-mobile-panel-features-content]'
      );
      addOffsetToStickyProductSlider('[data-product-base-poe-sticky-gallery]');
    }, 50)
  );

  window.addEventListener(
    'scroll',
    throttle(() => {
      addOffsetToStickyProductSlider('[data-product-base-poe-sticky-gallery]');
    }, 100)
  );

  document.addEventListener('DOMContentLoaded', () => {
    setMaxHeightStyle(
      '[data-product-base-poe-product-mobile-panel-features-content]'
    );
    addOffsetToStickyProductSlider('[data-product-base-poe-sticky-gallery]');
  });
};
